import {
  AlarmMediaResponse,
  Escalation,
  LocationAlarm,
  LocationAlarmIncidentDTO,
  MotionAlarm,
  UnifiedAlarm,
  UnifiedAlarmGroup,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';

export function transformLocationAlarmToUnifiedAlarmGroup(
  locationAlarm: LocationAlarm,
  rawAlarmToMediaMap?: Map<string, AlarmMediaResponse>
): UnifiedAlarmGroup {
  const alarms: UnifiedAlarm[] = locationAlarm.rawAlarms.map((alarm) =>
    transformRawAlarmToUnifiedAlarm(locationAlarm.id, alarm, rawAlarmToMediaMap)
  );

  return {
    id: locationAlarm.id,
    // Preserved from the alarm root, never derived from a camera or camera
    // group -- a v3 source group may be a camera group, a door, or an
    // alarm-derived fallback that exists in none of those tables.
    sourceGroupId: locationAlarm.sourceGroupId,
    locationAlarmTypeId: locationAlarm.locationAlarmTypeId,
    timestamp: locationAlarm.alarmTime,
    tenantId: locationAlarm.location.tenant_id ?? '',
    location: locationAlarm.location,
    alarms,
    status: locationAlarm.status,
    severity:
      locationAlarm.recommendation?.toLowerCase() === 'escalate'
        ? 'high'
        : locationAlarm.recommendation?.toLowerCase() === 'resolve'
        ? 'low'
        : '',
    // Every incident: `locationIncidents` carries every active incident at the
    // *location*, not just this alarm's group. Left unfiltered here -- the
    // scoping keys are preserved on each escalation (below) and callers narrow
    // with `isEscalationInScope` once they know which group is on screen.
    escalations: (locationAlarm?.locationIncidents ?? []).map(
      transformLocationAlarmIncidentToEscalation
    ),
    tapScore: locationAlarm.tap,
    // Without this the investigate view has no alarm type, so enterprise
    // groups render untyped there while Unified Monitoring, fed by the
    // socket, shows the type.
    locationAlarmType: locationAlarm.locationAlarmType,
    // Hardcoding REMOTE_GUARDING here made an enterprise alarm render as RG in
    // the investigate view while rendering as enterprise in Unified Monitoring.
    product: locationAlarm.product ?? UnifiedMonitoringProduct.REMOTE_GUARDING,
    abTests: locationAlarm.abTests,
  };
}

function transformLocationAlarmIncidentToEscalation(
  incident: LocationAlarmIncidentDTO
): Escalation {
  return {
    id: String(incident.id),
    title: incident.title ?? '',
    description: incident.description ?? '',
    incidentStartTimeUtc: incident.incidentStartTimeUtc ?? '',
    incidentEndTimeUtc: incident.incidentEndTimeUtc,
    initiatingLocationAlarmId: incident.initiatingLocationAlarmId ?? '',
    sourceGroupId: incident.sourceGroupId,
    locationAlarmTypeId: incident.locationAlarmTypeId,
  };
}

export function transformRawAlarmToUnifiedAlarm(
  locationAlarmId: string,
  alarm: MotionAlarm,
  rawAlarmToMediaMap?: Map<string, AlarmMediaResponse>
): UnifiedAlarm {
  // `sourceEntity` is whatever the alarm was raised on, which is only a camera
  // on remote guarding. An enterprise alarm is raised on a door, so taking the
  // camera fields from it labelled the timeline and the enterprise details card
  // with the door name -- duplicating Source Entity -- and put the door's id
  // and (absent) livestream where the camera's belong.
  //
  // Narrow to the door case rather than always preferring `camera`: remote
  // guarding repeats sourceEntity there, so this is a no-op for it either way,
  // and keying off the type says why the substitution exists.
  const camera =
    alarm.sourceEntity.type === 'DOOR'
      ? alarm.camera ?? alarm.sourceEntity
      : alarm.sourceEntity;
  const isTalkDownEnabled = Boolean(camera.audio_devices?.length);
  const alarmMedia = rawAlarmToMediaMap?.get(alarm.id);
  const framesData = alarm.media?.frames ?? [];
  const isFrames = framesData.length > 0;

  return {
    id: alarm.id,
    alarmGroupId: locationAlarmId,
    timestamp: alarm.timestamp,
    cameraId: camera.id,
    cameraName: camera.name || '',
    cameraGroupId: '', // Not available in source data
    cameraGroupName: '', // Not available in source data
    cameraLiveStreamUrl: camera.livestreamUrl || '',
    cameraPlayBackUrl: '', // Not available in source data
    // What the alarm was raised on, when that is not the camera. Kept so the
    // enterprise card can name the door and the camera separately; without it
    // its Source Entity row has nothing to render on this path.
    door: alarm.sourceEntity.type === 'DOOR' ? alarm.sourceEntity : undefined,
    alarmStartTime: alarm.videoStartTime
      ? new Date(alarm.videoStartTime).getTime()
      : isFrames
      ? framesData[0].timestamp
      : Date.now(),
    alarmEndTime: alarm.videoEndTime
      ? new Date(alarm.videoEndTime).getTime()
      : isFrames
      ? framesData[framesData.length - 1].timestamp
      : Date.now(),
    severity: '', // Not available in source data
    media: isFrames
      ? {
          type: 'frames',
          frames: framesData,
        }
      : {
          type: 'video',
          video: {
            url: alarmMedia?.url || '',
            boxes: alarmMedia?.boxes || [],
            startTime: alarm.videoStartTime || '',
            endTime: alarm.videoEndTime || '',
            matchedFaces: alarm.matchedFaces,
          },
          frames: [],
        },
    resolvedAlarm: alarm.status === 'Resolved by Hakimo',
    isTalkDownEnabled,
    tap: alarm.tap ?? undefined,
  };
}
